import { ImageUploadDropzone } from '@/components/shared/ImageUploadDropzone';
import { EntityFormShell, useEntityForm } from '@startupquickstart/frontend';
import type { AttractionType } from '@/config/objects/attraction-types';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormFieldGroup,
  Input,
  RichTextEditor,
  toast,
} from '@startupquickstart/frontend';
import { useNavigate, useParams } from 'react-router-dom';

interface AttractionTypeFormData extends Record<string, unknown> {
  name: string;
  description: string;
  featuredImageId: string | undefined;
}

const DEFAULTS: AttractionTypeFormData = {
  name: '',
  description: '',
  featuredImageId: undefined,
};

export function AttractionTypeFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const form = useEntityForm<AttractionTypeFormData, AttractionType>({
    resource: 'attraction-types',
    itemId,
    defaults: DEFAULTS,
    queryKey: 'AttractionType',
    fromApi: (a) => ({
      name: a.name ?? '',
      description: a.description ?? '',
      featuredImageId: a.featuredImageId ?? undefined,
    }),
    toApi: (f) => ({
      name: f.name.trim(),
      description: f.description || null,
      featuredImageId: f.featuredImageId || null,
    }),
    validate: (f) => (f.name.trim() ? {} : { name: 'Name is required' }),
    onSuccess: (entity, { isEdit: wasEdit }) => {
      if (wasEdit) {
        toast.success('Attraction type saved successfully');
        navigate(-1);
      } else {
        toast.success('Attraction type created successfully');
        if (entity.id) navigate(`/attraction-types/${entity.id}`);
      }
    },
    onNotFound: () => {
      toast.error('Attraction type not found');
      navigate('/attraction-types');
    },
  });

  return (
    <EntityFormShell
      title={isEdit ? 'Edit Attraction Type' : 'Create Attraction Type'}
      subtitle={
        isEdit
          ? form.formData.name || 'Update attraction type details'
          : 'Add a new attraction type to the platform'
      }
      backHref={isEdit && itemId ? `/attraction-types/${itemId}` : '/attraction-types'}
      form={form}
    >
      <div className="space-y-6">
        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">General Information</CardTitle>
            <CardDescription>Core attraction type details</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <FormFieldGroup name="name" label="Name" required error={form.errors.name}>
              <Input
                id="name"
                value={form.formData.name}
                onChange={(e) => form.updateField('name', e.target.value)}
                placeholder="Enter attraction type name"
                className={cn(form.errors.name && 'border-destructive')}
              />
            </FormFieldGroup>

            <ImageUploadDropzone
              targetAspectRatio={4 / 3}
              value={form.formData.featuredImageId}
              onChange={(value) => form.updateField('featuredImageId', value)}
            />

            <FormFieldGroup name="description" label="Description">
              <RichTextEditor
                value={form.formData.description}
                onChange={(value) => form.updateField('description', value ?? '')}
              />
            </FormFieldGroup>
          </CardContent>
        </Card>
      </div>
    </EntityFormShell>
  );
}
